import { componentByTypename } from '@/ui/utils/componentByTypename';
import { parsePriceVariant } from '@/ui/utils/parsePriceVariant';
import { parseNestedProductVariant } from '@/ui/components/pages/ProductDetail/utils/parseNestedProductVariant';
import {
  BundleItemDetailsWithUpsellingFragment,
  BundleProductsFragment,
  TileFragmentFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { BundleAdditionalInformation } from '@/ui/components/pages/ProductDetail/types/BundleAdditionalInformation';
import { BundleProduct } from '@/ui/services/BundleProductService';

type AdditionalInformationData = BundleItemDetailsWithUpsellingFragment['additionalInformation'];

// Every chunk of the CMS data is one product of the set, whether it parses or not.
export function countBundleSlots(
  bundleProductsData: BundleProductsFragment | null | undefined,
): number {
  return (
    componentByTypename(bundleProductsData?.content, 'ContentChunkContent')?.chunks.length ?? 0
  );
}

export function parseBundleProducts(
  bundleProductsData: BundleProductsFragment | null | undefined,
): BundleProduct[] {
  const bundleProductsRepeatableChunk = componentByTypename(
    bundleProductsData?.content,
    'ContentChunkContent',
  );
  if (!bundleProductsRepeatableChunk) {
    return [];
  }

  // Parse each product in the bundle
  return bundleProductsRepeatableChunk?.chunks
    .map((chunk, index) => {
      // Extract product information and discounts
      const productInfo = componentByTypename(chunk[0].content, 'ItemRelationsContent')?.items?.[0]; // first chunk is item relation to product, one product has to be selected
      const discounts = componentByTypename(chunk[1].content, 'PropertiesTableContent')
        ?.sections?.[0]?.properties; // second chunk is properties table, with one section defined in shape

      if (productInfo?.__typename !== 'Product' || !productInfo.variants) {
        return null;
      }

      const firstProductVariant =
        productInfo.variants.find((variant) => variant.isDefault) ?? productInfo.variants[0];

      const priceVariant = parsePriceVariant(firstProductVariant?.priceVariant);

      if (!firstProductVariant || !priceVariant) {
        return null;
      }

      const discount = Math.max(
        Number(discounts?.find((d) => d.key === priceVariant.identifier)?.value) || 0,
        0,
      );

      return new BundleProduct({
        bundleProductInstanceId: `${productInfo.id}_${index}`,
        id: productInfo.id,
        shape: productInfo.shape.identifier,
        path: productInfo.path!,
        name: productInfo.name!,
        discount,
        variants: productInfo.variants.map((variant) => parseNestedProductVariant(variant)),
      });
    })
    .filter((product): product is BundleProduct => !!product);
}

export function parseSubscriptions(
  bundleProductsData: BundleProductsFragment | null | undefined,
  priceVariantId: string | undefined,
) {
  const bundleProductsRepeatableChunk = componentByTypename(
    bundleProductsData?.content,
    'ContentChunkContent',
  );
  if (!bundleProductsRepeatableChunk) {
    return [];
  }

  return bundleProductsRepeatableChunk?.chunks
    .map((chunk, index) => {
      const productVariant = componentByTypename(chunk[0].content, 'ItemRelationsContent')
        ?.productVariants?.[0];

      if (!productVariant?.product) {
        return null;
      }

      const parsedProductVariant = parseNestedProductVariant(productVariant);

      const discounts = componentByTypename(chunk[1].content, 'PropertiesTableContent')
        ?.sections?.[0]?.properties;

      // The fetched price variant is the area's (query variable), so this resolves the same key
      // as parseBundleProducts; the argument only covers a pinned subscription without a price.
      const discountKey = parsedProductVariant.priceVariant?.identifier ?? priceVariantId;
      const discount = Math.max(
        Number(discounts?.find((d) => d.key === discountKey)?.value) || 0,
        0,
      );

      return new BundleProduct({
        bundleProductInstanceId: `${productVariant.product.id}_${index}`,
        id: productVariant.product.id,
        shape: productVariant.product.shape.identifier,
        path: productVariant.product.path!,
        name: productVariant.product.name!,
        discount,
        variants: [parsedProductVariant],
      });
    })
    .filter((product): product is BundleProduct => !!product);
}

export function parseAdditionalInformation(
  additionalInformationData: AdditionalInformationData,
): BundleAdditionalInformation | undefined {
  const additionalInformationSection = componentByTypename(
    additionalInformationData?.content,
    'ContentChunkContent',
  );

  if (!additionalInformationSection) {
    return undefined;
  }

  const additionalInformationText =
    componentByTypename(
      additionalInformationSection.chunks[0]?.find(
        (item) => item.id === 'additional-information-box',
      )?.content,
      'RichTextContent',
    ) ?? undefined;

  const additionalInformationTiles = componentByTypename(
    additionalInformationSection.chunks[0]?.find(
      (item) => item.id === 'additional-information-tiles',
    )?.content,
    'ItemRelationsContent',
  )?.items as TileFragmentFragment[];

  return {
    additionalInformationText,
    additionalInformationTiles,
  };
}
